import { useEffect } from "react";
import { isInsideInteractiveCellContent } from "../cells/cellControls";
import type { FocusQuiet, GridController } from "./gridController";
import { isNativeActivation } from "./keyMap";

/**
 * Grid keys after which focus shows: ones the grid handled and Enter or
 * Space on a control. Bare modifiers and keys the grid passed on (a host's
 * Shift+J) leave quiet focus quiet.
 */
export const isFocusRevealingKey = (event: {
    key: string;
    target: EventTarget | null;
    currentTarget: EventTarget | null;
    defaultPrevented: boolean;
}) => event.defaultPrevented || isNativeActivation(event);

// The range action keeps focus on the active cell, so its ring stays.
const isRangeActionPress = (target: Node) =>
    target instanceof Element &&
    target.closest("[data-dt-range-action]") !== null;

/** Whether focus lands on rows or on cells (`cellNavigation`). */
type FocusNavigation = "row" | "cell";

/**
 * Focus visuals after a pointer press in the rows. Row focus follows
 * `:focus-visible`: any press hides the row outline and reveals, so neither a
 * click nor focus returning from a row's menu outlines the row. A clicked
 * cell keeps its ring (it marks the cell edits and ranges start from); a
 * press on a control inside one hides only that ring.
 */
const PRESS_QUIET: Record<
    FocusNavigation,
    { control: FocusQuiet; plain: FocusQuiet | null }
> = {
    row: { control: "all", plain: "all" },
    cell: { control: "ring", plain: null },
};

/**
 * Pointer presses in the rows quiet focus visuals (`PRESS_QUIET`) until a
 * grid key shows them. Presses outside leave them alone, so picking from a
 * portaled popup doesn't ring the trigger focus returns to. Tab anywhere
 * shows them, so tabbing back into the grid shows focus. Pressing outside
 * the root, a portaled popup included, also ends focus-within, as focus
 * moving into that popup does; a closing editor returns focus itself.
 */
export const useFocusQuiet = ({
    controller,
    navigation,
}: {
    controller: GridController;
    navigation: FocusNavigation;
}) => {
    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            const { root, body } = controller;
            if (!root || !(event.target instanceof Node)) {
                return;
            }
            if (!root.contains(event.target)) {
                controller.isFocusWithin.set(false);
                return;
            }
            if (
                body?.contains(event.target) &&
                !isRangeActionPress(event.target)
            ) {
                const quiet = PRESS_QUIET[navigation];
                controller.setFocusQuiet(
                    isInsideInteractiveCellContent(event.target, body)
                        ? quiet.control
                        : quiet.plain,
                );
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Tab") {
                controller.setFocusQuiet(null);
            }
        };
        document.addEventListener("pointerdown", handlePointerDown, true);
        document.addEventListener("keydown", handleKeyDown, true);
        return () => {
            document.removeEventListener(
                "pointerdown",
                handlePointerDown,
                true,
            );
            document.removeEventListener("keydown", handleKeyDown, true);
        };
    }, [controller, navigation]);
};
